import { Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { PageState } from '../../shared/data/PageState';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { toPageError } from '../partners/PartnerListPage';
import { defaultWithdrawalCursorQuery, withdrawalsRepositoryToken } from './repository';
import { safeMaskedDiagnostic } from './types';

export function WithdrawalTimeline({
  language,
  withdrawalId,
}: {
  readonly language: TimeLanguage;
  readonly withdrawalId: string;
}) {
  const repository = useRepository(withdrawalsRepositoryToken);
  const { t } = useTranslation();
  const [cursor, setCursor] = useState('');
  const [history, setHistory] = useState<readonly string[]>(['']);
  const [index, setIndex] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    (signal) =>
      repository.listEvents(withdrawalId, { ...defaultWithdrawalCursorQuery, cursor }, signal),
    [cursor, refreshKey, repository, withdrawalId],
    (page) => page.items.length === 0 && index === 0,
  );
  return (
    <PageState
      emptyMessage={t('withdrawals.timeline.empty')}
      error={toPageError(resource.error, t('withdrawals.errors.events'))}
      onRetry={() => setRefreshKey((value) => value + 1)}
      status={resource.status}
    >
      {resource.data === null ? null : (
        <Stack spacing={1.5}>
          <Stack
            aria-label={t('withdrawals.timeline.label')}
            component="ol"
            spacing={1.5}
            sx={{ listStyle: 'none', m: 0, p: 0 }}
          >
            {resource.data.items.map((event) => {
              const time = formatTimestamp(event.occurredAt, 'Asia/Bangkok', language);
              return (
                <Paper component="li" key={event.id} sx={{ p: 2 }} variant="outlined">
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={1}>
                      <Chip label={`#${event.sequence}`} size="small" />
                      <Chip label={event.newStatus} size="small" />
                    </Stack>
                    <Typography>
                      {event.previousStatus ?? t('common.notAvailable')} → {event.newStatus}
                    </Typography>
                    {event.reasonCode === null ? null : (
                      <Typography variant="body2">{event.reasonCode}</Typography>
                    )}
                    {event.reason === null ? null : (
                      <Typography variant="body2">{safeMaskedDiagnostic(event.reason)}</Typography>
                    )}
                    {event.evidenceReference === null ? null : (
                      <Typography variant="body2">
                        {t('withdrawals.fields.evidence')}:{' '}
                        {safeMaskedDiagnostic(event.evidenceReference)}
                      </Typography>
                    )}
                    <Typography color="text.secondary" variant="caption">
                      {time.display} ({time.originalOffset}) · {event.actorId}
                    </Typography>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
          <CursorPageButtons
            index={index}
            nextCursor={resource.data.nextCursor}
            onMove={(nextIndex, nextCursor) => {
              setIndex(nextIndex);
              setCursor(nextCursor);
              if (nextIndex > index)
                setHistory((current) => [...current.slice(0, nextIndex), nextCursor]);
            }}
            previousCursor={history[index - 1] ?? ''}
          />
        </Stack>
      )}
    </PageState>
  );
}

export function CursorPageButtons({
  index,
  nextCursor,
  onMove,
  previousCursor,
}: {
  readonly index: number;
  readonly nextCursor: string | null;
  readonly onMove: (index: number, cursor: string) => void;
  readonly previousCursor: string;
}) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
      <Button disabled={index === 0} onClick={() => onMove(index - 1, previousCursor)}>
        {t('withdrawals.pagination.previous')}
      </Button>
      <Button
        disabled={nextCursor === null}
        onClick={() => {
          if (nextCursor !== null) onMove(index + 1, nextCursor);
        }}
      >
        {t('withdrawals.pagination.loadMore')}
      </Button>
    </Stack>
  );
}
